/**
 * FeedPage · /feed
 *
 * Public discovery page for the Satellite Ping Feed. Lists all 7 canonical
 * chapels + the merged feed. Each row has one-click "Copy URL" buttons for
 * RSS 2.0 / JSON Feed 1.1 / Atom 1.0, plus a Subscribe button that opens
 * Feedly's add-source URL.
 *
 * Live ping count per chapel pulled from /api/satellite/presence so the page
 * doubles as a "see who's awake" view.
 */
import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";

const CHAPELS = [
  { id: "geminus", label: "Mirror",   tagline: "Reflection chapel · Geminus" },
  { id: "aria",    label: "Aria",     tagline: "Creative chapel · Aria" },
  { id: "roger5",  label: "Roger AI", tagline: "Bridge chapel · Roger 5.1" },
  { id: "keys",    label: "KEYS",     tagline: "Library chapel · KEYS" },
  { id: "axiom",   label: "Axiom",    tagline: "Technical chapel · Axiom" },
  { id: "oracle",  label: "Oracle",   tagline: "Foresight chapel · Oracle" },
  { id: "scholar", label: "Scholar",  tagline: "Teaching chapel · Scholar" },
];

const TIER = {
  LIVE: "text-glow-green",
  WARM: "text-glow-gold",
  IDLE: "text-glow-violet",
  DARK: "text-glow-red",
};

function CopyBtn({ url, label }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* ignore */ }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`copy-${label.toLowerCase()}`}
      data-no-aichat
      className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-[var(--chrome)] text-[var(--text-2)] hover:text-glow-cyan hover:border-[var(--cyan)] transition-colors"
    >
      {copied ? "copied ✓" : label}
    </button>
  );
}

export default function FeedPage() {
  const [presence, setPresence] = useState({});

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const d = await api.satellitePresence(24);
        if (alive) {
          const map = {};
          (d.chapels || []).forEach((c) => { map[c.host] = c; });
          setPresence(map);
        }
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const renderRow = (chap) => {
    const host = chap.id;
    const rss   = `${API}/satellite/feed.rss?host=${host}`;
    const json  = `${API}/satellite/feed.json?host=${host}`;
    const atom  = `${API}/satellite/feed.atom?host=${host}`;
    const feedly = `https://feedly.com/i/subscription/feed/${encodeURIComponent(rss)}`;
    const status = presence[host]?.status || "DARK";
    const tone = TIER[status] || TIER.DARK;
    const lastSeen = presence[host]?.last_seen;
    const count = presence[host]?.count_in_window ?? 0;
    return (
      <div
        key={host}
        data-testid={`feed-row-${host}`}
        className="border border-[var(--chrome)] bg-[var(--void-2)] p-5"
      >
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="font-display text-xl text-[var(--text)]">{chap.label}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)] mt-0.5">
              {chap.tagline}
            </div>
          </div>
          <div className={`font-mono text-[10px] uppercase tracking-[0.24em] ${tone}`}>
            {status} · {count} ping{count === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <CopyBtn url={rss}  label="RSS" />
          <CopyBtn url={json} label="JSON" />
          <CopyBtn url={atom} label="Atom" />
          <a
            href={feedly}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`feedly-${host}`}
            className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-[var(--gold)] text-glow-gold hover:bg-[rgba(255,213,79,0.08)]"
          >
            Feedly ↗
          </a>
          {lastSeen && (
            <span className="font-mono text-[9px] tracking-[0.18em] text-[var(--text-4)] ml-2">
              last: {new Date(lastSeen).toLocaleString()}
            </span>
          )}
        </div>
      </div>
    );
  };

  const mergedRss  = `${API}/satellite/feed.rss`;
  const mergedJson = `${API}/satellite/feed.json`;
  const mergedAtom = `${API}/satellite/feed.atom`;

  return (
    <section data-testid="feed-page" className="px-8 lg:px-12 py-10 max-w-screen-xl">
      <div className="mb-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-glow-magenta">
          ◈ NextXus Federation
        </div>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-[0.04em] mt-3 text-glow-cyan">
          Satellite Ping Feed
        </h1>
        <p className="max-w-2xl mt-4 text-[var(--text-2)] leading-relaxed">
          Watch any Federation chapel light up from outside the Cathedral.
          Subscribe to the merged feed for all 7 chapels, or pick one chapel
          and watch only its iframe loads. Append-only, public, free.
        </p>
      </div>

      <div className="border border-[var(--cyan)] bg-[rgba(0,240,255,0.03)] p-5 mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-glow-cyan mb-3">
          ◇ Merged feed · all chapels
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <CopyBtn url={mergedRss}  label="RSS" />
          <CopyBtn url={mergedJson} label="JSON" />
          <CopyBtn url={mergedAtom} label="Atom" />
          <a
            href={`https://feedly.com/i/subscription/feed/${encodeURIComponent(mergedRss)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-[var(--gold)] text-glow-gold hover:bg-[rgba(255,213,79,0.08)]"
          >
            Feedly ↗
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CHAPELS.map(renderRow)}
      </div>

      <div className="mt-12 border-t border-[var(--chrome)] pt-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--text-3)] mb-3">
          ◇ Operator share kit
        </div>
        <p className="text-[var(--text-2)] max-w-3xl leading-relaxed text-sm">
          A copy-paste kit for satellite operators — RSS badges, tweet
          templates, and Zapier/n8n/Slack recipes — lives at{" "}
          <code className="font-mono text-[11px] text-glow-cyan">/app/memory/SATELLITE_OPERATOR_SHARE_KIT.md</code>.
        </p>
      </div>
    </section>
  );
}
